/**
 * Styles Tab - DataGrid Version with Full CRUD
 *
 * Uses Material-UI DataGrid for enhanced filtering, sorting, and pagination
 * Manages styles for a studio with multi-discipline support
 * (e.g., Musical Theatre can be both Acting AND Voice)
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetDisciplinesQuery,
  useGetStylesQuery,
  useCreateStyleMutation,
  useUpdateStyleMutation,
  useDeleteStyleMutation,
} from '../../../api/schedulingApi';
import type { Style } from '../../../types/scheduling';

interface StylesTabProps {
  studioId: string;
}

interface StyleFormData {
  title: string;
  description?: string;
  disciplines: string[];
}

interface StyleFormErrors {
  title?: string;
  description?: string;
  disciplines?: string;
}

const StylesTabDataGrid: React.FC<StylesTabProps> = ({ studioId }) => {
  const {
    data: styles,
    isLoading: stylesLoading,
    refetch: refetchStyles,
  } = useGetStylesQuery({
    studioId,
  });
  const { data: disciplines, isLoading: disciplinesLoading } =
    useGetDisciplinesQuery(studioId);

  // Force refetch on mount to ensure we have latest data
  React.useEffect(() => {
    refetchStyles();
  }, [refetchStyles]);
  const [createStyle, { isLoading: isCreating }] = useCreateStyleMutation();
  const [updateStyle, { isLoading: isUpdating }] = useUpdateStyleMutation();
  const [deleteStyle, { isLoading: isDeleting }] = useDeleteStyleMutation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState<Style | null>(null);
  const [deleteDialogStyle, setDeleteDialogStyle] = useState<Style | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState<StyleFormData>({
    title: '',
    description: '',
    disciplines: [],
  });

  const [formErrors, setFormErrors] = useState<StyleFormErrors>({});
  const [apiError, setApiError] = useState<string>('');

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      disciplines: [],
    });
    setFormErrors({});
    setApiError('');
  };

  const validateForm = (): boolean => {
    const errors: StyleFormErrors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    // Disciplines are optional - removed the validation check

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleOpenEditDialog = (style: Style) => {
    setEditingStyle(style);
    setFormData({
      title: style.title,
      description: style.description || '',
      disciplines: style.disciplines,
    });
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setEditingStyle(null);
    resetForm();
  };

  const handleDisciplineChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setFormData({
      ...formData,
      disciplines: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      setApiError('');
      await createStyle({
        studioId,
        data: {
          title: formData.title,
          description: formData.description || undefined,
          disciplines: formData.disciplines,
        },
      }).unwrap();
      handleCloseAddDialog();
    } catch (error: any) {
      console.error('Failed to create style:', error);

      // Handle duplicate style name error
      if (error?.data?.non_field_errors) {
        setApiError(
          `A style named "${formData.title}" already exists in this studio. Please choose a different name or edit the existing style.`
        );
      } else if (error?.data?.title) {
        setApiError(error.data.title[0]);
      } else {
        setApiError('Failed to create style. Please try again.');
      }
    }
  };

  const handleUpdate = async () => {
    if (!editingStyle || !validateForm()) return;

    try {
      await updateStyle({
        id: editingStyle.id,
        studioId,
        data: {
          title: formData.title,
          description: formData.description || undefined,
          disciplines: formData.disciplines,
        },
      }).unwrap();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Failed to update style:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialogStyle) return;

    try {
      await deleteStyle({
        id: deleteDialogStyle.id,
        studioId,
      }).unwrap();
      setDeleteDialogStyle(null);
    } catch (error) {
      console.error('Failed to delete style:', error);
    }
  };

  const getDisciplineName = useCallback(
    (disciplineId: string): string => {
      const discipline = disciplines?.find(d => d.id === disciplineId);
      return discipline?.title || disciplineId;
    },
    [disciplines]
  );

  // DataGrid columns configuration
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 200,
        renderCell: params => (
          <Typography variant="body2" color="text.secondary">
            {params.value || '—'}
          </Typography>
        ),
      },
      {
        field: 'disciplines',
        headerName: 'Disciplines',
        flex: 1,
        minWidth: 200,
        valueGetter: (value, row) => {
          const style = row as Style;
          // Return a string of all discipline names for filtering
          if (style.discipline_details && style.discipline_details.length > 0) {
            return style.discipline_details.map(d => d.title).join(', ');
          }
          return style.disciplines.map(id => getDisciplineName(id)).join(', ');
        },
        renderCell: params => {
          const style = params.row as Style;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {style.discipline_details && style.discipline_details.length > 0
                ? style.discipline_details.map(discipline => (
                    <Chip
                      key={discipline.id}
                      label={discipline.title}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                : style.disciplines.map(disciplineId => (
                    <Chip
                      key={disciplineId}
                      label={getDisciplineName(disciplineId)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
            </Stack>
          );
        },
      },
      {
        field: 'class_count',
        headerName: 'Classes Count',
        width: 130,
        renderCell: params => (
          <Chip
            label={`${params.value} classes`}
            size="small"
            color={params.value > 0 ? 'primary' : 'default'}
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: (params: GridRowParams<Style>) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleOpenEditDialog(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => setDeleteDialogStyle(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [getDisciplineName]
  );

  if (stylesLoading || disciplinesLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6">Styles</Typography>
          <Typography variant="body2" color="text.secondary">
            Dance styles and classes that can belong to multiple disciplines
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          disabled={!disciplines || disciplines.length === 0}
        >
          Add Style
        </Button>
      </Stack>

      {(() => {
        if (!disciplines || disciplines.length === 0) {
          return (
            <Alert severity="warning">
              You must create disciplines first before adding styles. Go to the
              Disciplines tab to create discipline categories.
            </Alert>
          );
        }
        if (!styles || styles.length === 0) {
          return (
            <Alert severity="info">
              No styles added yet. Click &quot;Add Style&quot; to create your
              first dance style.
            </Alert>
          );
        }
        return (
          <Paper sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={styles}
              columns={columns}
              loading={stylesLoading}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
              }}
            />
          </Paper>
        );
      })()}

      {/* Add Style Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Style</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {apiError && (
              <Alert severity="error" onClose={() => setApiError('')}>
                {apiError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={e =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!!formErrors.title}
              helperText={formErrors.title || 'e.g., Ballet, Jazz, Tap'}
              required
              autoFocus
            />
            <FormControl fullWidth error={!!formErrors.disciplines}>
              <InputLabel>Disciplines</InputLabel>
              <Select
                multiple
                value={formData.disciplines}
                onChange={handleDisciplineChange}
                input={<OutlinedInput label="Disciplines" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => (
                      <Chip
                        key={value}
                        label={getDisciplineName(value)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {disciplines?.map(discipline => (
                  <MenuItem key={discipline.id} value={discipline.id}>
                    {discipline.title}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.disciplines ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.disciplines}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Optional: Select one or more disciplines (e.g., Musical
                  Theatre can be Acting + Voice)
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              helperText="Optional description of this style"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreating}
          >
            Add Style
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Style Dialog */}
      <Dialog
        open={!!editingStyle}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Style</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={e =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!!formErrors.title}
              helperText={formErrors.title || 'e.g., Ballet, Jazz, Tap'}
              required
              autoFocus
            />
            <FormControl fullWidth error={!!formErrors.disciplines}>
              <InputLabel>Disciplines</InputLabel>
              <Select
                multiple
                value={formData.disciplines}
                onChange={handleDisciplineChange}
                input={<OutlinedInput label="Disciplines" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => (
                      <Chip
                        key={value}
                        label={getDisciplineName(value)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {disciplines?.map(discipline => (
                  <MenuItem key={discipline.id} value={discipline.id}>
                    {discipline.title}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.disciplines ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.disciplines}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Optional: Select one or more disciplines (e.g., Musical
                  Theatre can be Acting + Voice)
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              helperText="Optional description of this style"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isUpdating}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialogStyle}
        onClose={() => setDeleteDialogStyle(null)}
      >
        <DialogTitle>Delete Style?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deleteDialogStyle?.title}
            &quot;?
          </Typography>
          {deleteDialogStyle && deleteDialogStyle.class_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This style has {deleteDialogStyle.class_count} associated classes.
              Deleting it may affect those classes.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogStyle(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StylesTabDataGrid;
